/**
 * ExerciseAnimation - Illustrations animées SVG pour chaque exercice HIIT
 * Stick figures animés avec CSS keyframes
 */

import React, { memo } from 'react';

// Map exercise IDs to animation groups
const ANIMATION_MAP = {
  // Squats
  bodyweight_squat: 'squat',
  soft_squat_jump: 'squatJump',
  squat_jumps: 'squatJump',
  pistol_squat: 'squat',
  // Push-ups
  knee_pushup: 'pushup',
  classic_pushup: 'pushup',
  clap_pushup: 'pushup',
  hspu: 'pushup',
  // Planks
  static_plank: 'plank',
  dynamic_plank: 'plank',
  plank_shoulder_tap: 'plankTap',
  // Jumping
  jumping_jacks: 'jumpingJacks',
  slow_jacks: 'jumpingJacks',
  box_jumps: 'boxJump',
  high_box_jumps: 'boxJump',
  double_unders: 'jumpRope',
  // Running / knees
  march: 'march',
  slow_knees: 'highKnees',
  high_knees: 'highKnees',
  sprint_place: 'highKnees',
  // Lunges
  slow_lunges: 'lunge',
  alt_jump_lunges: 'lunge',
  // Burpees
  burpee_no_jump: 'burpee',
  full_burpee: 'burpee',
  burpee_pullup: 'burpee',
  // Mountain climbers
  slow_climbers: 'climber',
  fast_climbers: 'climber',
  // Steps / cardio
  step_touches: 'stepTouch',
  shoulder_rotation: 'shoulderRotation',
  // Advanced
  thruster: 'thruster',
  man_makers: 'burpee',
  bear_crawl: 'bearCrawl',
  muscle_ups: 'pullup',
  // Elite
  clean_jerk: 'thruster',
  snatch: 'thruster',
  assault_bike: 'highKnees',
  turkish_getup: 'squat',
  sled_push: 'bearCrawl',
  tabata_full: 'jumpingJacks',
  complex_barbell: 'thruster',
};

function getAnimationType(exerciseId) {
  return ANIMATION_MAP[exerciseId] || 'jumpingJacks';
}

// --- SVG Stick Figure Animations ---

function SquatAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <style>{`
        @keyframes squat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(15px); }
        }
        .squat-body { animation: squat 1.5s ease-in-out infinite; }
        @keyframes squat-legs {
          0%, 100% { d: path("M50 75 L40 100 L35 115"); }
          50% { d: path("M50 75 L35 90 L30 105"); }
        }
      `}</style>
      <g className="squat-body">
        {/* Head */}
        <circle cx="50" cy="20" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        {/* Body */}
        <line x1="50" y1="30" x2="50" y2="70" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms out */}
        <line x1="50" y1="45" x2="25" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="45" x2="75" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Left leg */}
        <line x1="50" y1="70" x2="35" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="95;85;95" dur="1.5s" repeatCount="indefinite"/>
        </line>
        <line x1="35" y1="95" x2="30" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y1" values="95;85;95" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="y2" values="112;100;112" dur="1.5s" repeatCount="indefinite"/>
        </line>
        {/* Right leg */}
        <line x1="50" y1="70" x2="65" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="95;85;95" dur="1.5s" repeatCount="indefinite"/>
        </line>
        <line x1="65" y1="95" x2="70" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y1" values="95;85;95" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="y2" values="112;100;112" dur="1.5s" repeatCount="indefinite"/>
        </line>
      </g>
    </svg>
  );
}

function SquatJumpAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,-12; 0,0; 0,10; 0,0" dur="1.2s" repeatCount="indefinite"/>
        <circle cx="50" cy="20" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="30" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="42" x2="28" y2="35" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="35;25;35;50;35" dur="1.2s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="42" x2="72" y2="35" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="35;25;35;50;35" dur="1.2s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="65" x2="38" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="38" y1="90" x2="35" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="62" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="62" y1="90" x2="65" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function PushupAnim() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <g>
        {/* Head */}
        <circle cx="25" cy="25" r="8" fill="none" stroke="#f97316" strokeWidth="2.5">
          <animate attributeName="cy" values="25;35;25" dur="1.8s" repeatCount="indefinite"/>
        </circle>
        {/* Body horizontal */}
        <line x1="33" y1="28" x2="80" y2="45" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y1" values="28;38;28" dur="1.8s" repeatCount="indefinite"/>
        </line>
        {/* Arms */}
        <line x1="40" y1="32" x2="35" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y1" values="32;42;32" dur="1.8s" repeatCount="indefinite"/>
          <animate attributeName="x2" values="35;38;35" dur="1.8s" repeatCount="indefinite"/>
        </line>
        <line x1="55" y1="38" x2="50" y2="60" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y1" values="38;45;38" dur="1.8s" repeatCount="indefinite"/>
        </line>
        {/* Legs */}
        <line x1="80" y1="45" x2="105" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="105" y1="50" x2="112" y2="60" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Ground */}
        <line x1="10" y1="62" x2="115" y2="62" stroke="#f9731640" strokeWidth="1" strokeDasharray="4,4"/>
      </g>
    </svg>
  );
}

function PlankAnim() {
  return (
    <svg viewBox="0 0 120 70" className="w-full h-full">
      <circle cx="22" cy="28" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="30" y1="30" x2="82" y2="38" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="38;36;38" dur="2s" repeatCount="indefinite"/>
      </line>
      <line x1="38" y1="33" x2="35" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="82" y1="38" x2="105" y2="42" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="105" y1="42" x2="110" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="10" y1="54" x2="115" y2="54" stroke="#f9731640" strokeWidth="1" strokeDasharray="4,4"/>
      {/* Breathing indicator */}
      <circle cx="55" cy="35" r="3" fill="#f9731630" stroke="none">
        <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/>
      </circle>
    </svg>
  );
}

function PlankTapAnim() {
  return (
    <svg viewBox="0 0 120 70" className="w-full h-full">
      <circle cx="22" cy="28" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="30" y1="30" x2="82" y2="38" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Left arm tapping shoulder */}
      <line x1="38" y1="33" x2="35" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x2" values="35;45;35" dur="1.5s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="52;30;52" dur="1.5s" repeatCount="indefinite"/>
      </line>
      {/* Right arm stable */}
      <line x1="55" y1="36" x2="52" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="82" y1="38" x2="105" y2="42" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="105" y1="42" x2="110" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="10" y1="54" x2="115" y2="54" stroke="#f9731640" strokeWidth="1" strokeDasharray="4,4"/>
    </svg>
  );
}

function JumpingJacksAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,-5; 0,0" dur="0.8s" repeatCount="indefinite"/>
        <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="28" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms */}
        <line x1="50" y1="40" x2="25" y2="30" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="25;40;25" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="y2" values="30;55;30" dur="0.8s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="40" x2="75" y2="30" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="75;60;75" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="y2" values="30;55;30" dur="0.8s" repeatCount="indefinite"/>
        </line>
        {/* Legs */}
        <line x1="50" y1="65" x2="30" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="30;45;30" dur="0.8s" repeatCount="indefinite"/>
        </line>
        <line x1="30" y1="95" x2="25" y2="115" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x1" values="30;45;30" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="x2" values="25;43;25" dur="0.8s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="65" x2="70" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="70;55;70" dur="0.8s" repeatCount="indefinite"/>
        </line>
        <line x1="70" y1="95" x2="75" y2="115" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x1" values="70;55;70" dur="0.8s" repeatCount="indefinite"/>
          <animate attributeName="x2" values="75;57;75" dur="0.8s" repeatCount="indefinite"/>
        </line>
      </g>
    </svg>
  );
}

function HighKneesAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="50" y1="28" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Arms swinging */}
      <line x1="50" y1="42" x2="35" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="55;35;55" dur="0.5s" repeatCount="indefinite"/>
      </line>
      <line x1="50" y1="42" x2="65" y2="35" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="35;55;35" dur="0.5s" repeatCount="indefinite"/>
      </line>
      {/* Left leg - high knee */}
      <line x1="50" y1="65" x2="40" y2="85" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="85;55;85" dur="0.5s" repeatCount="indefinite"/>
        <animate attributeName="x2" values="40;45;40" dur="0.5s" repeatCount="indefinite"/>
      </line>
      <line x1="40" y1="85" x2="38" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y1" values="85;55;85" dur="0.5s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="112;80;112" dur="0.5s" repeatCount="indefinite"/>
      </line>
      {/* Right leg - grounded alternating */}
      <line x1="50" y1="65" x2="60" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="90;65;90" dur="0.5s" repeatCount="indefinite" begin="0.25s"/>
      </line>
      <line x1="60" y1="90" x2="62" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y1" values="90;65;90" dur="0.5s" repeatCount="indefinite" begin="0.25s"/>
        <animate attributeName="y2" values="112;85;112" dur="0.5s" repeatCount="indefinite" begin="0.25s"/>
      </line>
    </svg>
  );
}

function MarchAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="50" y1="28" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Arms gentle swing */}
      <line x1="50" y1="42" x2="35" y2="58" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="58;48;58" dur="1s" repeatCount="indefinite"/>
      </line>
      <line x1="50" y1="42" x2="65" y2="48" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="48;58;48" dur="1s" repeatCount="indefinite"/>
      </line>
      {/* Legs marching */}
      <line x1="50" y1="65" x2="42" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="90;75;90" dur="1s" repeatCount="indefinite"/>
      </line>
      <line x1="42" y1="90" x2="40" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y1" values="90;75;90" dur="1s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="112;98;112" dur="1s" repeatCount="indefinite"/>
      </line>
      <line x1="50" y1="65" x2="58" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y2" values="90;75;90" dur="1s" repeatCount="indefinite" begin="0.5s"/>
      </line>
      <line x1="58" y1="90" x2="60" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y1" values="90;75;90" dur="1s" repeatCount="indefinite" begin="0.5s"/>
        <animate attributeName="y2" values="112;98;112" dur="1s" repeatCount="indefinite" begin="0.5s"/>
      </line>
    </svg>
  );
}

function LungeAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,8; 0,0" dur="2s" repeatCount="indefinite"/>
        <circle cx="50" cy="15" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="25" x2="50" y2="60" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms on hips */}
        <line x1="50" y1="38" x2="38" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="38" x2="62" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Front leg bent */}
        <line x1="50" y1="60" x2="35" y2="82" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="35" y1="82" x2="30" y2="105" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Back leg extended */}
        <line x1="50" y1="60" x2="72" y2="80" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="72" y1="80" x2="80" y2="105" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function BurpeeAnim() {
  return (
    <svg viewBox="0 0 120 100" className="w-full h-full">
      {/* Phase 1: Standing */}
      <g opacity="1">
        <animate attributeName="opacity" values="1;0;0;0;1" dur="2.5s" repeatCount="indefinite"/>
        <circle cx="60" cy="15" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="60" y1="23" x2="60" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="35" x2="45" y2="48" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="35" x2="75" y2="48" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="55" x2="50" y2="80" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="55" x2="70" y2="80" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
      {/* Phase 2: Plank */}
      <g opacity="0">
        <animate attributeName="opacity" values="0;0;1;0;0" dur="2.5s" repeatCount="indefinite"/>
        <circle cx="20" cy="30" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="28" y1="33" x2="85" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="42" y1="38" x2="38" y2="60" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="85" y1="50" x2="100" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="100" y1="55" x2="105" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
      {/* Phase 3: Jump */}
      <g opacity="0">
        <animate attributeName="opacity" values="0;0;0;1;0" dur="2.5s" repeatCount="indefinite"/>
        <circle cx="60" cy="5" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="60" y1="13" x2="60" y2="42" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="25" x2="40" y2="15" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="25" x2="80" y2="15" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="42" x2="48" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="60" y1="42" x2="72" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function ClimberAnim() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <circle cx="22" cy="25" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="30" y1="28" x2="65" y2="38" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Arms */}
      <line x1="35" y1="30" x2="30" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="34" x2="48" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Left leg - driving forward */}
      <line x1="65" y1="38" x2="45" y2="45" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x2" values="45;80;45" dur="0.6s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="45;48;45" dur="0.6s" repeatCount="indefinite"/>
      </line>
      <line x1="45" y1="45" x2="42" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x1" values="45;80;45" dur="0.6s" repeatCount="indefinite"/>
        <animate attributeName="x2" values="42;85;42" dur="0.6s" repeatCount="indefinite"/>
      </line>
      {/* Right leg - alternating */}
      <line x1="65" y1="38" x2="80" y2="48" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x2" values="80;45;80" dur="0.6s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="48;45;48" dur="0.6s" repeatCount="indefinite"/>
      </line>
      <line x1="80" y1="48" x2="85" y2="55" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x1" values="80;45;80" dur="0.6s" repeatCount="indefinite"/>
        <animate attributeName="x2" values="85;42;85" dur="0.6s" repeatCount="indefinite"/>
      </line>
      <line x1="10" y1="56" x2="115" y2="56" stroke="#f9731640" strokeWidth="1" strokeDasharray="4,4"/>
    </svg>
  );
}

function BoxJumpAnim() {
  return (
    <svg viewBox="0 0 120 110" className="w-full h-full">
      {/* Box */}
      <rect x="55" y="70" width="40" height="28" rx="3" fill="#f9731620" stroke="#f97316" strokeWidth="1.5"/>
      {/* Figure */}
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 25,-30; 25,0; 0,0" dur="1.8s" repeatCount="indefinite"/>
        <circle cx="30" cy="30" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="30" y1="38" x2="30" y2="60" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="30" y1="48" x2="18" y2="40" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="40;30;40;40" dur="1.8s" repeatCount="indefinite"/>
        </line>
        <line x1="30" y1="48" x2="42" y2="40" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="40;30;40;40" dur="1.8s" repeatCount="indefinite"/>
        </line>
        <line x1="30" y1="60" x2="22" y2="82" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="22" y1="82" x2="20" y2="98" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="30" y1="60" x2="38" y2="82" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="38" y1="82" x2="40" y2="98" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function JumpRopeAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,-10; 0,0" dur="0.5s" repeatCount="indefinite"/>
        <circle cx="50" cy="20" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="30" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Hands with rope */}
        <line x1="50" y1="42" x2="32" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="42" x2="68" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="43" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="43" y1="90" x2="42" y2="108" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="57" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="57" y1="90" x2="58" y2="108" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
      {/* Rope arc */}
      <ellipse cx="50" cy="70" rx="30" ry="40" fill="none" stroke="#f9731660" strokeWidth="1.5" strokeDasharray="5,5">
        <animateTransform attributeName="transform" type="rotate" values="0 50 70; 360 50 70" dur="0.5s" repeatCount="indefinite"/>
      </ellipse>
    </svg>
  );
}

function StepTouchAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="-8,0; 8,0; -8,0" dur="1.2s" repeatCount="indefinite"/>
        <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="28" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="42" x2="35" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="42" x2="65" y2="52" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="42" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="42" y1="90" x2="40" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="58" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="58" y1="90" x2="60" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function ShoulderRotationAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
      <line x1="50" y1="28" x2="50" y2="68" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Rotating arms */}
      <line x1="50" y1="40" x2="30" y2="35" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x2" values="30;35;50;65;70;65;50;35;30" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="35;20;15;20;35;55;60;55;35" dur="2s" repeatCount="indefinite"/>
      </line>
      <line x1="50" y1="40" x2="70" y2="35" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="x2" values="70;65;50;35;30;35;50;65;70" dur="2s" repeatCount="indefinite"/>
        <animate attributeName="y2" values="35;20;15;20;35;55;60;55;35" dur="2s" repeatCount="indefinite"/>
      </line>
      {/* Legs */}
      <line x1="50" y1="68" x2="42" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="42" y1="95" x2="40" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="50" y1="68" x2="58" y2="95" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="58" y1="95" x2="60" y2="112" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function ThrusterAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,10; 0,0; 0,10" dur="1.5s" repeatCount="indefinite"/>
        <circle cx="50" cy="18" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="28" x2="50" y2="65" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms pressing overhead */}
        <line x1="50" y1="38" x2="30" y2="28" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="28;10;28" dur="1.5s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="38" x2="70" y2="28" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="28;10;28" dur="1.5s" repeatCount="indefinite"/>
        </line>
        {/* Barbell */}
        <line x1="22" y1="28" x2="78" y2="28" stroke="#f9731680" strokeWidth="3" strokeLinecap="round">
          <animate attributeName="y1" values="28;10;28" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="y2" values="28;10;28" dur="1.5s" repeatCount="indefinite"/>
        </line>
        {/* Legs */}
        <line x1="50" y1="65" x2="38" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="38" y1="90" x2="35" y2="108" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="65" x2="62" y2="90" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="62" y1="90" x2="65" y2="108" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function BearCrawlAnim() {
  return (
    <svg viewBox="0 0 120 80" className="w-full h-full">
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 5,0; 0,0; -5,0; 0,0" dur="1s" repeatCount="indefinite"/>
        <circle cx="25" cy="22" r="8" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        {/* Body angled */}
        <line x1="33" y1="25" x2="72" y2="32" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms crawling */}
        <line x1="38" y1="27" x2="30" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="30;40;30" dur="0.5s" repeatCount="indefinite"/>
        </line>
        <line x1="52" y1="30" x2="55" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="55;45;55" dur="0.5s" repeatCount="indefinite"/>
        </line>
        {/* Legs crawling */}
        <line x1="72" y1="32" x2="65" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="65;75;65" dur="0.5s" repeatCount="indefinite"/>
        </line>
        <line x1="72" y1="32" x2="85" y2="50" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="x2" values="85;75;85" dur="0.5s" repeatCount="indefinite"/>
        </line>
      </g>
      <line x1="5" y1="52" x2="115" y2="52" stroke="#f9731640" strokeWidth="1" strokeDasharray="4,4"/>
    </svg>
  );
}

function PullupAnim() {
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full">
      {/* Bar */}
      <line x1="20" y1="10" x2="80" y2="10" stroke="#f9731680" strokeWidth="3" strokeLinecap="round"/>
      {/* Figure */}
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0; 0,-20; 0,0" dur="2s" repeatCount="indefinite"/>
        <circle cx="50" cy="30" r="10" fill="none" stroke="#f97316" strokeWidth="2.5"/>
        <line x1="50" y1="40" x2="50" y2="75" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Arms reaching up to bar */}
        <line x1="50" y1="45" x2="35" y2="18" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="18;30;18" dur="2s" repeatCount="indefinite"/>
        </line>
        <line x1="50" y1="45" x2="65" y2="18" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round">
          <animate attributeName="y2" values="18;30;18" dur="2s" repeatCount="indefinite"/>
        </line>
        {/* Legs */}
        <line x1="50" y1="75" x2="42" y2="100" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="50" y1="75" x2="58" y2="100" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

// Animation component map
const ANIMATIONS = {
  squat: SquatAnim,
  squatJump: SquatJumpAnim,
  pushup: PushupAnim,
  plank: PlankAnim,
  plankTap: PlankTapAnim,
  jumpingJacks: JumpingJacksAnim,
  highKnees: HighKneesAnim,
  march: MarchAnim,
  lunge: LungeAnim,
  burpee: BurpeeAnim,
  climber: ClimberAnim,
  boxJump: BoxJumpAnim,
  jumpRope: JumpRopeAnim,
  stepTouch: StepTouchAnim,
  shoulderRotation: ShoulderRotationAnim,
  thruster: ThrusterAnim,
  bearCrawl: BearCrawlAnim,
  pullup: PullupAnim,
};

export const ExerciseAnimation = memo(function ExerciseAnimation({ exerciseId, className = '' }) {
  const animType = getAnimationType(exerciseId);
  const AnimComponent = ANIMATIONS[animType] || JumpingJacksAnim;

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <AnimComponent />
    </div>
  );
});

export default ExerciseAnimation;
